import React, { useState } from 'react';

const promptTemplates = {
  investigate: ({ report, item }) => `I want to deeply investigate this opportunity from my Lokyis Opportunity Radar.\n\nReport: ${report.reportDate} — ${report.edition}\nOpportunity: ${item.name}\nOverall score: ${item.scores.overall}/10\n24h freshness: ${item.scores.day}\n7d momentum: ${item.scores.week}\n30d validation: ${item.scores.month}\n90d durability: ${item.scores.quarter}\nBuyer: ${item.buyer}\nProblem: ${item.problem}\nCurrent product hypothesis: ${item.product}\nCurrent recommended action: ${item.action}\n\nPlease do fresh web research and investigate this opportunity in depth. Check buyer pain, existing competitors, Reddit/community complaints where useful, current products and pricing, UK-specific relevance, willingness to pay, market gaps, distribution options, and risks. Tell me whether the score should rise or fall and give me a clear recommendation: Drop, Watch, Investigate, Validate, or Prototype. Do not just agree with the existing radar conclusion.`,
  validate: ({ report, item }) => `Help me validate this opportunity from my Lokyis Opportunity Radar before I build it.\n\nReport: ${report.reportDate} — ${report.edition}\nOpportunity: ${item.name}\nOverall score: ${item.scores.overall}/10\nBuyer: ${item.buyer}\nProblem: ${item.problem}\nProduct hypothesis: ${item.product}\n\nBuild a practical validation plan for a solo founder. Define the narrowest target customer, strongest pain hypothesis, 10 interview questions, where to find potential users, competitor checks, pricing hypotheses, a landing-page test, success/failure thresholds, and what evidence would justify moving this from validation to prototype.`,
  build: ({ report, item }) => `Turn this opportunity from my Lokyis Opportunity Radar into a lean build plan.\n\nReport: ${report.reportDate} — ${report.edition}\nOpportunity: ${item.name}\nOverall score: ${item.scores.overall}/10\nBuyer: ${item.buyer}\nProblem: ${item.problem}\nProduct hypothesis: ${item.product}\nRecommended action: ${item.action}\n\nAssume I am a solo/small-team software developer. Propose the smallest credible MVP, must-have vs later features, user flow, key screens, data model, suggested architecture/stack, third-party APIs, major risks, a 2–4 week implementation sequence, and what not to build yet. Keep it commercially focused rather than over-engineered.`,
};

const actionLabels = {
  investigate: 'Deep investigate',
  validate: 'Validate idea',
  build: 'Build plan',
};

async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function FounderActions({ report, item }) {
  const [copiedPrompt, setCopiedPrompt] = useState('');
  const [copiedLabel, setCopiedLabel] = useState('');
  const [copyError, setCopyError] = useState('');

  const copyPrompt = async (type) => {
    const prompt = promptTemplates[type]({ report, item });
    setCopiedPrompt(prompt);
    setCopiedLabel(actionLabels[type]);
    setCopyError('');

    try {
      await copyText(prompt);
    } catch (error) {
      setCopyError('Automatic copy failed. The full prompt is shown below so you can copy it manually.');
    }
  };

  return (
    <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Founder actions</div>
        <p className="mt-1 text-xs leading-5 text-slate-500">Choose an action to copy a ready-made prompt. You stay on this page.</p>
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        <button onClick={() => copyPrompt('investigate')} className="rounded-xl bg-slate-900 px-3 py-2.5 text-xs font-bold text-white transition hover:bg-slate-800">🔍 Deep investigate</button>
        <button onClick={() => copyPrompt('validate')} className="rounded-xl border border-violet-200 bg-violet-50 px-3 py-2.5 text-xs font-bold text-violet-700 transition hover:bg-violet-100">🧪 Validate idea</button>
        <button onClick={() => copyPrompt('build')} className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs font-bold text-emerald-700 transition hover:bg-emerald-100">🛠 Build plan</button>
      </div>

      {copiedPrompt && (
        <div className="mt-4 overflow-hidden rounded-2xl border border-emerald-200 bg-emerald-50/70">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-emerald-100 px-4 py-3">
            <div>
              <div className="text-xs font-bold text-emerald-800">✓ {copiedLabel} prompt copied</div>
              <div className="mt-0.5 text-[11px] text-emerald-700/70">Paste this into ChatGPT when you are ready.</div>
            </div>
            <button
              onClick={() => copyText(copiedPrompt)}
              className="rounded-lg border border-emerald-200 bg-white px-2.5 py-1.5 text-[11px] font-bold text-emerald-700 hover:bg-emerald-50"
            >
              Copy again
            </button>
          </div>
          <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words px-4 py-4 text-xs leading-5 text-slate-700">{copiedPrompt}</pre>
        </div>
      )}

      {copyError && <p className="mt-3 rounded-xl bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">{copyError}</p>}
    </div>
  );
}
